import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { IMAGE_ENDPOINT } from "../../helpers/constants";
import {
  Card,
  Header,
  Brand,
  Tagline,
  UploadZone,
  UploadIconWrapper,
  UploadText,
  UploadHint,
  PreviewContainer,
  PreviewImage,
  Divider,
  ResultsSection,
  SectionLabel,
  Caption,
  TagContainer,
  Tag,
  UploadButton,
  LoaderRow,
  Spinner,
} from "./ImageUpload.styles";

export default function ImageUpload() {
  const [displayImage, setDisplayImage] = useState(null);
  const [image, setImage] = useState(null);
  const [imageCaption, setImageCaption] = useState(null);
  const [imageTags, setImageTags] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const fileInput = useRef(null);

  function processFile(file) {
    if (!file) return;
    if (file.size > 4000000) {
      alert("Max file size is 4MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }
    setImageCaption(null);
    setImageTags(null);
    setImage(file);
  }

  function handleFileChange(event) {
    processFile(event.target.files[0]);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }

  useEffect(() => {
    if (!image) return;

    setDisplayImage(URL.createObjectURL(image));
    setLoading(true);

    axios
      .post(IMAGE_ENDPOINT, image, {
        headers: { "content-type": image.type },
      })
      .then((res) => {
        setImageCaption(res.data.description.captions[0]);
        setImageTags(res.data.description.tags);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        alert("Something went wrong analysing your image. Please try again.");
      });

    return () => {
      setImage(null);
    };
  }, [image]);

  const hasResults = imageCaption || (imageTags && imageTags.length > 0);

  return (
    <Card>
      <Header>
        <Brand>caption.ai</Brand>
        <Tagline>Upload an image and let AI describe it for you</Tagline>
      </Header>

      {displayImage ? (
        <PreviewContainer>
          <PreviewImage src={displayImage} alt="Uploaded preview" />
        </PreviewContainer>
      ) : (
        <UploadZone
          $dragging={dragging}
          onClick={() => fileInput.current.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <UploadIconWrapper>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(167,139,250,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </UploadIconWrapper>
          <UploadText>
            <strong>Click to upload</strong> or drag and drop
          </UploadText>
          <UploadHint>PNG, JPG, WEBP — max 4MB</UploadHint>
        </UploadZone>
      )}

      {loading && (
        <LoaderRow>
          <Spinner />
          Analysing your image…
        </LoaderRow>
      )}

      {hasResults && (
        <>
          <Divider />
          <ResultsSection>
            {imageCaption && imageCaption.text && (
              <div>
                <SectionLabel>AI Caption</SectionLabel>
                <Caption>{imageCaption.text}</Caption>
              </div>
            )}
            {imageTags && imageTags.length > 0 && (
              <div>
                <SectionLabel>Tags</SectionLabel>
                <TagContainer>
                  {imageTags.map((tag) =>
                    tag && typeof tag === "string" ? (
                      <Tag key={tag}>{tag}</Tag>
                    ) : null
                  )}
                </TagContainer>
              </div>
            )}
          </ResultsSection>
        </>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <UploadButton onClick={() => fileInput.current.click()}>
        {displayImage ? "Upload a different image" : "Choose image"}
      </UploadButton>
    </Card>
  );
}
